import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Boxes, Wrench, GraduationCap } from "lucide-react";
import { COLORS } from "@/lib/site";

const CONTENT = {
  eyebrow: "AI-first problem solving · Mysuru",
  h1a: "AI, applied to ",
  h1b: "small business",
  h1c: " problems.",
  sub: "Aumonline helps Indian MSMEs solve real business problems using AI — as software you subscribe to, as bespoke solutions built for you, or as a course that teaches you to build them yourself.",
  chips: ["Solutions", "Bespoke", "Academy"],
  ctaPrimary: "Bring us your problem",
  ctaSecondary: "See what we've solved",
};

/* ============================================================
   V2 — DARK EDITORIAL  (near-black, serif, glow accents)
============================================================ */
export function V2Dark() {
  return (
    <div className="bg-[#0A0F1C] text-white min-h-[100vh]" data-testid="v2-dark">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-40 pointer-events-none"
             style={{backgroundImage:"radial-gradient(600px circle at 15% 20%, rgba(0,132,61,0.20), transparent 60%), radial-gradient(500px circle at 85% 30%, rgba(255,184,28,0.10), transparent 60%)"}} />
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
             style={{backgroundImage:"linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)", backgroundSize:"48px 48px"}}/>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-20 md:pt-28 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 text-xs font-medium mb-6 px-3 py-1 rounded-full border border-white/15 bg-white/5">
              <Sparkles size={14} className="text-[#FFB81C]" /> {CONTENT.eyebrow}
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
              {CONTENT.h1a}<span className="italic text-[#7DDFA0]">{CONTENT.h1b}</span>{CONTENT.h1c}
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">{CONTENT.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {CONTENT.chips.map(c => (
                <span key={c} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium">{c}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-md px-6 py-3 font-medium text-[#0A0F1C]" style={{backgroundColor:"#7DDFA0"}}>{CONTENT.ctaPrimary} <ArrowRight size={18}/></Link>
              <Link to="/products" className="inline-flex items-center gap-2 rounded-md px-6 py-3 font-medium border border-white/25 hover:bg-white/5">{CONTENT.ctaSecondary}</Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Live pilot</div>
              <div className="mt-1 font-serif-display text-2xl">Wekreate Space</div>
              <div className="mt-2 text-sm text-slate-300">5 hubs · 600+ seats · powered by Kreta</div>
              <div className="mt-6 h-40 rounded-lg" style={{background:"conic-gradient(from 210deg at 60% 40%, rgba(125,223,160,.5), rgba(0,51,160,.35), rgba(255,184,28,.3), rgba(125,223,160,.5))", filter:"blur(1px)"}}/>
              <div className="mt-4 text-xs text-slate-400">"Built and proven inside a real co-working operation."</div>
            </div>
          </div>
        </div>
      </section>
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 grid md:grid-cols-3 gap-6">
          {[{i:Boxes,t:"Solutions",d:"Software you subscribe to."},{i:Wrench,t:"Bespoke",d:"Solved for your business."},{i:GraduationCap,t:"Academy",d:"Learn to build with AI."}].map(x=>(
            <div key={x.t} className="rounded-xl border border-white/10 bg-white/[0.03] p-6 hover:bg-white/[0.06] transition">
              <x.i size={24} className="text-[#7DDFA0]"/>
              <div className="mt-3 font-serif-display text-xl">{x.t}</div>
              <div className="mt-1 text-sm text-slate-400">{x.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ============================================================
   V3 — BENTO GRID  (structured, dev-startup feel, mono accents)
============================================================ */
export function V3Bento() {
  return (
    <div className="bg-[#FAFAF7] min-h-[100vh]" data-testid="v3-bento">
      <section className="max-w-7xl mx-auto px-4 md:px-8 pt-16 md:pt-20 pb-10 grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-5">— {CONTENT.eyebrow}</div>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight text-[#0A0F1C] leading-[1.02]">
            AI, applied to <span className="text-[#00843D]">small business</span> problems.
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">{CONTENT.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md px-5 py-3 font-medium text-white" style={{backgroundColor:COLORS.blue}}>{CONTENT.ctaPrimary} <ArrowRight size={18}/></Link>
            <Link to="/products" className="inline-flex items-center gap-2 rounded-md px-5 py-3 font-medium border" style={{borderColor:COLORS.green, color:COLORS.green}}>{CONTENT.ctaSecondary}</Link>
          </div>
        </div>
        <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-4 h-[520px]">
          <div className="col-span-4 row-span-3 rounded-2xl border border-gray-200 bg-white p-6 flex flex-col justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">01 · Solutions</div>
              <div className="mt-2 text-2xl font-semibold text-[#0A0F1C]">Software you subscribe to.</div>
              <p className="mt-2 text-sm text-slate-600">Kreta · Apoopika · Jigyasa — live SaaS built for MSMEs. 14-day free trial.</p>
            </div>
            <div className="flex gap-2">
              {["Kreta","Apoopika","Jigyasa"].map(n => <span key={n} className="text-xs font-mono px-2 py-1 rounded bg-slate-100 text-slate-700">{n}</span>)}
            </div>
          </div>
          <div className="col-span-2 row-span-3 rounded-2xl p-6 text-white flex flex-col justify-between" style={{backgroundColor:COLORS.blue}}>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/70">02 · Bespoke</div>
              <div className="mt-2 text-xl font-semibold leading-tight">Solved for your business.</div>
            </div>
            <div className="text-xs text-white/80">Construction · Clinics · Events · Service</div>
          </div>
          <div className="col-span-2 row-span-3 rounded-2xl border border-gray-200 bg-[#0A0F1C] text-white p-6 flex flex-col justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-white/60">03 · Academy</div>
              <div className="mt-2 text-xl font-semibold leading-tight">Learn to build with AI.</div>
            </div>
            <div className="text-xs text-white/60">Prompt · RAG · MCP · Agents · Vibe coding</div>
          </div>
          <div className="col-span-4 row-span-3 rounded-2xl border border-gray-200 bg-white p-6 flex flex-col justify-between">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">LIVE PILOT</div>
              <div className="mt-2 text-2xl font-semibold text-[#0A0F1C]">Wekreate Space</div>
              <p className="mt-2 text-sm text-slate-600">5 hubs · 600+ seats · running on Kreta.</p>
            </div>
            <div className="text-xs text-slate-500 font-mono">// proof, not marketing</div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ============================================================
   V4 — CINEMATIC  (full-bleed photo, big serif, minimal words)
============================================================ */
export function V4Cinematic() {
  return (
    <div className="bg-[#FDFCF0] min-h-[100vh]" data-testid="v4-cinematic">
      <section className="relative h-[560px] md:h-[640px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{background:"linear-gradient(180deg, rgba(10,15,28,.55) 0%, rgba(10,15,28,.35) 40%, rgba(10,15,28,.85) 100%)"}}/>
        <div className="relative h-full max-w-6xl mx-auto px-4 md:px-8 flex flex-col justify-end pb-16 text-white">
          <div className="inline-flex items-center gap-2 text-xs font-medium mb-5 px-3 py-1 rounded-full border border-white/25 bg-black/20 backdrop-blur w-fit">
            <Sparkles size={14} className="text-[#FFB81C]" /> {CONTENT.eyebrow}
          </div>
          <h1 className="font-serif-display text-6xl md:text-7xl lg:text-8xl font-semibold leading-[0.98] tracking-tight max-w-5xl">
            AI, applied to<br/><span className="italic text-[#FFB81C]">small business</span> problems.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl">{CONTENT.sub}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-md px-6 py-3 font-medium text-[#0A0F1C]" style={{backgroundColor:"#FFB81C"}}>{CONTENT.ctaPrimary} <ArrowRight size={18}/></Link>
            <Link to="/products" className="inline-flex items-center gap-2 rounded-md px-6 py-3 font-medium border border-white/50 text-white hover:bg-white/10">{CONTENT.ctaSecondary}</Link>
          </div>
        </div>
      </section>
      <section className="bg-[#FDFCF0] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 grid md:grid-cols-3 gap-8">
          {[{i:Boxes,t:"Solutions",d:"Software you subscribe to — Kreta, Apoopika, Jigyasa."},{i:Wrench,t:"Bespoke",d:"Solved for your business — construction, clinics, events."},{i:GraduationCap,t:"Academy",d:"Learn to build with AI — from prompts to shipping."}].map(x=>(
            <div key={x.t} className="border-t-2 border-[#0A0F1C] pt-5">
              <x.i size={22} style={{color:COLORS.blue}}/>
              <div className="mt-2 font-serif-display text-2xl text-[#0A0F1C]">{x.t}</div>
              <div className="mt-1 text-sm text-slate-600 leading-relaxed">{x.d}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
